import { useMemo, useState, type ReactNode } from 'react';
import { jtBillingUrl } from '../../api/client';
import { MarketingContactUsage } from './MarketingContactUsage';
import {
  useMarketingContacts,
  useMarketingSuppressions,
  useAddMarketingContact,
  useBulkImportMarketingContacts,
  useRemoveMarketingContact,
  useUnsuppressMarketing,
  type AddMarketingContactPayload,
  type BulkImportResult,
} from '../../api/marketing-contacts.api';

/**
 * Section 10.4 — marketing contacts admin surface.
 *
 * Single source of truth for an org's opted-in newsletter audience. The list
 * is intentionally separate from the outreach contact cache: a name showing
 * up here means "they've explicitly opted in to bulk emails", not "we have
 * their record". Removal stamps the suppression table — Section 14
 * isolation says marketing opt-out must NOT cross-suppress outreach.
 */

export function MarketingContactsSection() {
  const [includeUnsub, setIncludeUnsub] = useState(false);
  const [view, setView] = useState<'contacts' | 'suppressions'>('contacts');

  return (
    <SectionShell
      title="Marketing contacts"
      subtitle="Organisation-level list managed by admins, shared across the workspace rather than per user. Recipients of newsletter and bulk campaigns. Kept separate from outreach: unsubscribes here never affect direct recruiter outreach. (Phase 2: a JobTetris-side flag will mark marketing contacts so they can be pulled across from the primary app.)"
    >
      <div style={{ display: 'flex', gap: 'var(--space-2)', borderBottom: '1px solid var(--color-border-default)', paddingBottom: 'var(--space-2)' }}>
        <Tab active={view === 'contacts'} onClick={() => setView('contacts')}>Contacts</Tab>
        <Tab active={view === 'suppressions'} onClick={() => setView('suppressions')}>Suppressions</Tab>
      </div>

      {view === 'contacts' && (
        <ContactsView includeUnsub={includeUnsub} onToggleUnsub={setIncludeUnsub} />
      )}
      {view === 'suppressions' && <SuppressionsView />}
    </SectionShell>
  );
}

function ContactsView({ includeUnsub, onToggleUnsub }: { includeUnsub: boolean; onToggleUnsub: (v: boolean) => void }) {
  const { data: contacts, isLoading } = useMarketingContacts({ includeUnsubscribed: includeUnsub });
  const remove = useRemoveMarketingContact();
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState('');

  // Client-side filter — list endpoint already caps at 500, and most orgs
  // will fit comfortably under that. If lists grow beyond that, the right
  // fix is server-side search rather than infinite scroll on the client.
  const filteredContacts = useMemo(() => {
    if (!contacts) return [];
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => {
      const haystack = [
        c.email,
        c.first_name,
        c.last_name,
        c.title,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [contacts, search]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <MarketingContactUsage />
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            <input type="checkbox" checked={includeUnsub} onChange={(e) => onToggleUnsub(e.target.checked)} />
            Show unsubscribed
          </label>
          <SecondaryButton onClick={() => setImporting(true)}>Import CSV</SecondaryButton>
          <PrimaryButton onClick={() => setAdding(true)}>Add contact</PrimaryButton>
        </div>
      </div>

      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by email, name, or title…"
        style={{
          height: 'var(--input-height, 38px)',
          padding: '0 var(--space-3)',
          borderRadius: 'var(--radius-input)',
          border: '1px solid var(--color-border-default)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-sm)',
        }}
      />

      {isLoading && <div style={{ padding: 'var(--space-4)' }}>Loading…</div>}
      {!isLoading && (contacts?.length ?? 0) === 0 && (
        <div style={{ padding: 'var(--space-6)', border: '1px dashed var(--color-border-strong)', borderRadius: 'var(--radius-input)', textAlign: 'center', color: 'var(--color-text-muted)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
          No marketing contacts yet. Add candidates or contacts who've opted in to your newsletter list, or import a CSV.
        </div>
      )}
      {!isLoading && (contacts?.length ?? 0) > 0 && filteredContacts.length === 0 && (
        <div style={{ padding: 'var(--space-4)', textAlign: 'center', color: 'var(--color-text-muted)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
          No matches for "{search}".
        </div>
      )}

      {filteredContacts.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Title</Th>
              <Th>Opt-in</Th>
              <Th>Status</Th>
              <Th>{' '}</Th>
            </tr>
          </thead>
          <tbody>
            {filteredContacts.map((c) => {
              const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ');
              return (
                <tr key={c.id}>
                  <Td>{fullName || <span style={{ color: 'var(--color-text-muted)' }}>-</span>}</Td>
                  <Td><code style={{ fontSize: 'var(--text-xs)' }}>{c.email}</code></Td>
                  <Td>{c.title || <span style={{ color: 'var(--color-text-muted)' }}>-</span>}</Td>
                  <Td>{c.opt_in_method.replace('_', ' ').toLowerCase()}</Td>
                  <Td>
                    {c.unsubscribed_at
                      ? <span style={{ color: 'var(--color-text-muted)' }}>Unsubscribed</span>
                      : <span style={{ color: 'var(--color-success)', fontWeight: 'var(--font-weight-semibold)' }}>Active</span>
                    }
                  </Td>
                  <Td>
                    {!c.unsubscribed_at && (
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Unsubscribe ${c.email} from marketing? This will not affect outreach.`)) {
                            remove.mutate(c.id);
                          }
                        }}
                        disabled={remove.isPending}
                        style={smallButtonStyle}
                      >
                        Unsubscribe
                      </button>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {adding && <AddContactModal onClose={() => setAdding(false)} />}
      {importing && <ImportCsvModal onClose={() => setImporting(false)} />}
    </div>
  );
}

/* ── CSV import modal ──────────────────────────────────────────────────── */

interface CsvRow {
  email: string;
  first_name: string;
  last_name: string;
  title: string;
  /** Optional — empty means "freeform" (no JT linkage). */
  contact_ref_id: string;
  /** 'candidate' / 'contact' when linked, 'external' for freeform. */
  contact_type: 'candidate' | 'contact' | 'external';
}

/**
 * Light CSV parser — RFC4180-ish: handles quoted fields, escaped quotes (""),
 * and Windows / Unix line endings. Header row required. Throws on
 * unparseable input rather than dropping rows silently.
 */
function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const out: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ',') {
        row.push(field);
        field = '';
      } else if (ch === '\n' || ch === '\r') {
        // Push the in-progress row when we hit any line terminator, skip
        // the LF half of a CRLF pair.
        if (ch === '\r' && text[i + 1] === '\n') i++;
        row.push(field);
        field = '';
        out.push(row);
        row = [];
      } else {
        field += ch;
      }
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    out.push(row);
  }
  if (out.length === 0) throw new Error('CSV is empty.');
  const headers = out[0].map((h) => h.trim().toLowerCase());
  return { headers, rows: out.slice(1) };
}

function ImportCsvModal({ onClose }: { onClose: () => void }) {
  const importer = useBulkImportMarketingContacts();
  const [csvText, setCsvText] = useState('');
  const [parsed, setParsed] = useState<{ rows: CsvRow[]; warnings: string[] } | null>(null);
  const [consent, setConsent] = useState(false);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleParse = () => {
    setError(null);
    setResult(null);
    try {
      const { headers, rows } = parseCsv(csvText);
      // Map common header variants → our payload shape. Anything not
      // recognised becomes a warning but doesn't block import.
      const colIndex = (names: string[]) => {
        for (const n of names) {
          const idx = headers.indexOf(n);
          if (idx !== -1) return idx;
        }
        return -1;
      };
      const emailCol = colIndex(['email', 'email_address']);
      const firstCol = colIndex(['first_name', 'first name', 'firstname']);
      const lastCol = colIndex(['last_name', 'last name', 'lastname', 'surname']);
      const titleCol = colIndex(['title', 'position', 'job_title']);
      const refIdCol = colIndex(['contact_ref_id', 'jt_id', 'id', 'jt_candidate_id', 'jt_contact_id']);
      const typeCol = colIndex(['contact_type', 'record_type', 'type']);

      if (emailCol === -1) {
        setError('CSV must include an "email" column.');
        return;
      }
      // contact_ref_id is now optional — rows without one become freeform
      // (external) marketing contacts.

      const warnings: string[] = [];
      const out: CsvRow[] = [];
      for (let i = 0; i < rows.length; i++) {
        const cols = rows[i];
        const email = (cols[emailCol] ?? '').trim();
        if (!email) {
          warnings.push(`Row ${i + 2}: missing email, skipped`);
          continue;
        }
        const refId = refIdCol === -1 ? '' : (cols[refIdCol] ?? '').trim();
        // When refId is missing we mark the row 'external'. When present, the
        // explicit type column wins; otherwise default to candidate.
        let contact_type: 'candidate' | 'contact' | 'external';
        if (!refId) {
          contact_type = 'external';
        } else {
          const raw = typeCol === -1 ? 'candidate' : (cols[typeCol] ?? 'candidate').trim().toLowerCase();
          contact_type = raw === 'contact' ? 'contact' : 'candidate';
        }
        out.push({
          email,
          first_name: (firstCol === -1 ? '' : cols[firstCol] ?? '').trim(),
          last_name: (lastCol === -1 ? '' : cols[lastCol] ?? '').trim(),
          title: (titleCol === -1 ? '' : cols[titleCol] ?? '').trim(),
          contact_ref_id: refId,
          contact_type,
        });
      }
      if (out.length === 0) {
        setError('No usable rows found in the CSV.');
        return;
      }
      setParsed({ rows: out, warnings });
    } catch (err) {
      setError((err as Error).message ?? 'Could not parse CSV');
    }
  };

  const handleImport = async () => {
    if (!parsed || !consent) return;
    setError(null);
    try {
      const payload: AddMarketingContactPayload[] = parsed.rows.map((r) => ({
        email: r.email,
        first_name: r.first_name || null,
        last_name: r.last_name || null,
        title: r.title || null,
        // Only attach the link fields for rows that actually had a ref id —
        // freeform rows leave them off so the server stores contact_type
        // = 'external' with a null ref id.
        ...(r.contact_ref_id
          ? { contact_ref_id: r.contact_ref_id, contact_type: r.contact_type }
          : {}),
        opt_in_method: 'CSV',
      }));
      const res = await importer.mutateAsync(payload);
      setResult(res);
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
        ?? (err as Error).message
        ?? 'Import failed';
      setError(message);
    }
  };

  return (
    <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal)', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--modal-overlay-bg)', padding: 'var(--space-4)' }}>
      <div style={{ width: '100%', maxWidth: 640, maxHeight: '90vh', backgroundColor: 'var(--card-bg)', borderRadius: 'var(--card-radius)', boxShadow: '0 24px 48px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column' }}>
        <header style={{ padding: 'var(--space-5) var(--space-6)', borderBottom: '1px solid var(--color-border-default)' }}>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)' }}>
            Import marketing contacts (CSV)
          </h2>
          <p style={{ marginTop: '2px', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Required header: <code>email</code>. Optional: <code>first_name</code>, <code>last_name</code>, <code>title</code>, <code>contact_ref_id</code>, <code>contact_type</code>. Rows without a <code>contact_ref_id</code> are saved as freeform (no JT linkage).
          </p>
        </header>

        <div style={{ padding: 'var(--space-5) var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', overflowY: 'auto' }}>
          {!result && (
            <>
              <Field label="CSV content" hint="Paste the CSV text here. The first row must be a header row.">
                <textarea
                  value={csvText}
                  onChange={(e) => { setCsvText(e.target.value); setParsed(null); }}
                  placeholder={'email,first_name,last_name,title\nada@example.com,Ada,Lovelace,CTO'}
                  rows={8}
                  style={{
                    width: '100%',
                    padding: 'var(--space-2) var(--space-3)',
                    borderRadius: 'var(--radius-input)',
                    border: '1px solid var(--color-border-default)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--text-xs)',
                    resize: 'vertical',
                  }}
                />
              </Field>

              {!parsed && (
                <SecondaryButton onClick={handleParse}>Parse CSV</SecondaryButton>
              )}

              {parsed && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  <div style={{ padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-input)', backgroundColor: 'var(--color-success-bg, #e8f7ed)', border: '1px solid var(--color-success-border, #a8d8b9)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
                    Parsed <strong>{parsed.rows.length}</strong> ready-to-import {parsed.rows.length === 1 ? 'row' : 'rows'}.
                  </div>
                  {parsed.warnings.length > 0 && (
                    <details style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                      <summary>{parsed.warnings.length} skipped row{parsed.warnings.length === 1 ? '' : 's'} during parse</summary>
                      <ul style={{ margin: 'var(--space-2) 0 0 var(--space-4)' }}>
                        {parsed.warnings.slice(0, 20).map((w, i) => <li key={i}>{w}</li>)}
                        {parsed.warnings.length > 20 && <li>…and {parsed.warnings.length - 20} more</li>}
                      </ul>
                    </details>
                  )}

                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', padding: 'var(--space-3)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-input)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: '4px' }} />
                    <span>
                      I confirm every contact in this CSV has explicitly opted in to receive marketing emails from us.
                      Importing without consent risks anti-spam violations and unsubscribe complaints.
                    </span>
                  </label>
                </div>
              )}
            </>
          )}

          {result && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <div style={{ padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-input)', backgroundColor: 'var(--color-success-bg, #e8f7ed)', border: '1px solid var(--color-success-border, #a8d8b9)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
                Added <strong>{result.added}</strong>. Skipped <strong>{result.skipped.length}</strong>.
                {result.aborted_at_row !== undefined && (
                  <>
                    {' '}Stopped at row {result.aborted_at_row + 1}.{' '}
                    {result.limit_message ?? 'This billing month has reached its marketing-contact limit.'}{' '}
                    <a href={jtBillingUrl()} target="_blank" rel="noreferrer" style={{ color: 'var(--color-text-brand)' }}>Buy more</a>
                  </>
                )}
              </div>
              {result.skipped.length > 0 && (
                <details style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                  <summary>Skipped rows</summary>
                  <ul style={{ margin: 'var(--space-2) 0 0 var(--space-4)' }}>
                    {result.skipped.slice(0, 50).map((s, i) => <li key={i}><code>{s.email}</code>: {s.reason}</li>)}
                    {result.skipped.length > 50 && <li>…and {result.skipped.length - 50} more</li>}
                  </ul>
                </details>
              )}
            </div>
          )}

          {error && (
            <div role="alert" style={{ padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-input)', border: '1px solid var(--color-warning)', color: 'var(--color-warning)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
              {error}
            </div>
          )}
        </div>

        <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)', padding: 'var(--space-4) var(--space-6)', borderTop: '1px solid var(--color-border-default)' }}>
          <SecondaryButton onClick={onClose}>{result ? 'Done' : 'Cancel'}</SecondaryButton>
          {!result && parsed && (
            <PrimaryButton onClick={handleImport} disabled={!consent || importer.isPending}>
              {importer.isPending ? 'Importing…' : `Import ${parsed.rows.length} contacts`}
            </PrimaryButton>
          )}
        </footer>
      </div>
    </div>
  );
}

function SuppressionsView() {
  const { data: rows, isLoading } = useMarketingSuppressions();
  const unsuppress = useUnsuppressMarketing();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-input)', backgroundColor: 'var(--color-surface-muted)', color: 'var(--color-text-muted)' }}>
        Addresses on this list are blocked from <strong>newsletter</strong> sends only.
        Outreach suppression lives in a separate list and is not affected.
      </div>

      {isLoading && <div style={{ padding: 'var(--space-4)' }}>Loading…</div>}
      {!isLoading && (rows?.length ?? 0) === 0 && (
        <div style={{ padding: 'var(--space-6)', border: '1px dashed var(--color-border-strong)', borderRadius: 'var(--radius-input)', textAlign: 'center', color: 'var(--color-text-muted)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
          No marketing suppressions.
        </div>
      )}

      {(rows?.length ?? 0) > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
          <thead>
            <tr>
              <Th>Email</Th>
              <Th>Reason</Th>
              <Th>Source</Th>
              <Th>Suppressed at</Th>
              <Th>{' '}</Th>
            </tr>
          </thead>
          <tbody>
            {rows!.map((r) => (
              <tr key={r.id}>
                <Td><code style={{ fontSize: 'var(--text-xs)' }}>{r.email}</code></Td>
                <Td>{r.reason.toLowerCase()}</Td>
                <Td><code style={{ fontSize: 'var(--text-xs)' }}>{r.source}</code></Td>
                <Td>{new Date(r.suppressed_at).toLocaleDateString()}</Td>
                <Td>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Remove ${r.email} from the marketing suppression list?`)) {
                        unsuppress.mutate(r.email);
                      }
                    }}
                    disabled={unsuppress.isPending}
                    style={smallButtonStyle}
                  >
                    Remove
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ── Add contact modal ─────────────────────────────────────────────────── */

function AddContactModal({ onClose }: { onClose: () => void }) {
  const add = useAddMarketingContact();
  // JT linkage is opt-in now — most recruiters are adding freeform
  // newsletter subscribers from webforms / hand-typed lists. The "Link to
  // a JT record" details collapse out of the way by default; only users
  // who care about candidate-timeline integration touch them.
  const [form, setForm] = useState<AddMarketingContactPayload>({
    email: '',
    first_name: '',
    last_name: '',
    title: '',
    opt_in_method: 'MANUAL',
  });
  const [linkOpen, setLinkOpen] = useState(false);
  const [refId, setRefId] = useState('');
  const [refType, setRefType] = useState<'candidate' | 'contact'>('candidate');
  const [error, setError] = useState<string | null>(null);

  function patch<K extends keyof AddMarketingContactPayload>(key: K, value: AddMarketingContactPayload[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit() {
    setError(null);
    if (!form.email) {
      setError('Email is required.');
      return;
    }
    try {
      await add.mutateAsync({
        ...form,
        // Only attach the link fields when the user actually filled them in.
        ...(linkOpen && refId.trim()
          ? { contact_ref_id: refId.trim(), contact_type: refType }
          : {}),
      });
      onClose();
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
        ?? (err as Error).message
        ?? 'Failed to add contact';
      setError(message);
    }
  }

  return (
    <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal)', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--modal-overlay-bg)', padding: 'var(--space-4)' }}>
      <div style={{ width: '100%', maxWidth: 480, backgroundColor: 'var(--card-bg)', borderRadius: 'var(--card-radius)', boxShadow: '0 24px 48px rgba(0,0,0,0.12)' }}>
        <header style={{ padding: 'var(--space-5) var(--space-6)', borderBottom: '1px solid var(--color-border-default)' }}>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)' }}>
            Add marketing contact
          </h2>
          <p style={{ marginTop: '2px', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            By adding this contact you confirm they've opted in to receive marketing emails.
          </p>
        </header>

        <div style={{ padding: 'var(--space-5) var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <Field label="Email">
            <Input value={form.email} type="email" onChange={(v) => patch('email', v)} placeholder="subscriber@example.com" />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
            <Field label="First name" hint="Used as {{first_name}} in templates.">
              <Input value={form.first_name ?? ''} onChange={(v) => patch('first_name', v)} placeholder="Sienna" />
            </Field>
            <Field label="Last name" hint="Used as {{last_name}} in templates.">
              <Input value={form.last_name ?? ''} onChange={(v) => patch('last_name', v)} placeholder="Lee" />
            </Field>
          </div>

          <Field label="Title / position" hint="Used as {{title}} or {{role_name}} in templates.">
            <Input value={form.title ?? ''} onChange={(v) => patch('title', v)} placeholder="Senior Backend Engineer" />
          </Field>

          <Field label="Opt-in method">
            <select
              value={form.opt_in_method ?? 'MANUAL'}
              onChange={(e) => patch('opt_in_method', e.target.value as AddMarketingContactPayload['opt_in_method'])}
              style={{
                height: 'var(--input-height, 38px)',
                padding: '0 var(--space-3)',
                borderRadius: 'var(--radius-input)',
                border: '1px solid var(--color-border-default)',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
              }}
            >
              <option value="MANUAL">Manual tagging</option>
              <option value="BULK_IMPORT">Bulk import</option>
              <option value="CSV">CSV import</option>
            </select>
          </Field>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <button
              type="button"
              onClick={() => setLinkOpen((v) => !v)}
              style={{
                alignSelf: 'flex-start',
                padding: 'var(--space-1) var(--space-2)',
                borderRadius: 'var(--btn-radius)',
                border: 'none',
                backgroundColor: 'transparent',
                color: 'var(--color-text-link)',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: 'pointer',
              }}
            >
              {linkOpen ? '− Hide JT link' : '+ Link to a JT record (optional)'}
            </button>
            {linkOpen && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', padding: 'var(--space-3)', borderRadius: 'var(--radius-input)', backgroundColor: 'var(--color-surface-muted)', border: '1px solid var(--color-border-default)' }}>
                <Field label="JT record ID" hint="Paste the candidate or contact id from a JT URL. Leave blank to keep this contact freeform.">
                  <Input value={refId} onChange={setRefId} placeholder="abc123…" />
                </Field>
                <Field label="Record type">
                  <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <ChipBtn active={refType === 'candidate'} onClick={() => setRefType('candidate')}>Candidate</ChipBtn>
                    <ChipBtn active={refType === 'contact'} onClick={() => setRefType('contact')}>Contact</ChipBtn>
                  </div>
                </Field>
              </div>
            )}
          </div>

          {error && (
            <div role="alert" style={{ padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-input)', border: '1px solid var(--color-warning)', color: 'var(--color-warning)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
              {error}
            </div>
          )}
        </div>

        <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)', padding: 'var(--space-4) var(--space-6)', borderTop: '1px solid var(--color-border-default)' }}>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} disabled={add.isPending}>{add.isPending ? 'Adding…' : 'Add'}</PrimaryButton>
        </footer>
      </div>
    </div>
  );
}

/* ── Local primitives ──────────────────────────────────────────────────── */

function SectionShell({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) {
  return (
    <section style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 'var(--card-radius)', boxShadow: 'var(--card-shadow)', overflow: 'hidden' }}>
      <header style={{ padding: 'var(--space-5) var(--space-6)', borderBottom: '1px solid var(--color-border-default)' }}>
        <h2 style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)' }}>{title}</h2>
        <p style={{ marginTop: 'var(--space-1)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{subtitle}</p>
      </header>
      <div style={{ padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>{children}</div>
    </section>
  );
}

function Tab({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} style={{
      padding: 'var(--space-2) var(--space-3)',
      backgroundColor: 'transparent',
      border: 'none',
      borderBottom: `2px solid ${active ? 'var(--color-action-primary)' : 'transparent'}`,
      marginBottom: '-1px',
      color: active ? 'var(--color-text-brand)' : 'var(--color-text-muted)',
      fontFamily: 'var(--font-sans)',
      fontSize: 'var(--text-sm)',
      fontWeight: active ? 'var(--font-weight-semibold)' : 'var(--font-weight-medium)',
      cursor: 'pointer',
    }}>{children}</button>
  );
}

function Th({ children }: { children: ReactNode }) {
  return <th style={{ textAlign: 'left', padding: 'var(--space-2) var(--space-3)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border-default)' }}>{children}</th>;
}
function Td({ children }: { children: ReactNode }) {
  return <td style={{ padding: 'var(--space-2) var(--space-3)', borderBottom: '1px solid var(--color-border-default)' }}>{children}</td>;
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>{label}</span>
      {children}
      {hint && <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{hint}</span>}
    </div>
  );
}

function Input({ value, type = 'text', placeholder, onChange }: { value: string; type?: string; placeholder?: string; onChange: (v: string) => void }) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={{
        height: 'var(--input-height, 38px)',
        padding: '0 var(--space-3)',
        borderRadius: 'var(--radius-input)',
        border: '1px solid var(--color-border-default)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)',
      }}
    />
  );
}

function ChipBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} style={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 'var(--btn-height)',
      padding: '0 var(--btn-padding-x)',
      borderRadius: 'var(--btn-radius)',
      backgroundColor: active ? 'var(--color-action-primary)' : 'var(--color-surface-white)',
      border: `1px solid ${active ? 'var(--color-action-primary)' : 'var(--color-border-default)'}`,
      color: active ? 'var(--color-action-primary-text)' : 'var(--color-text-secondary)',
      fontFamily: 'var(--font-sans)',
      fontSize: 'var(--text-sm)',
      fontWeight: 'var(--font-weight-semibold)',
      cursor: 'pointer',
    }}>{children}</button>
  );
}

function PrimaryButton({ onClick, disabled, children }: { onClick?: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 'var(--btn-height)',
      padding: '0 var(--btn-padding-x)',
      borderRadius: 'var(--btn-radius)',
      backgroundColor: 'var(--color-action-primary)',
      color: 'var(--color-action-primary-text)',
      border: 'none',
      fontFamily: 'var(--font-sans)',
      fontSize: 'var(--btn-font-size)',
      fontWeight: 'var(--btn-font-weight)',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
    }}>{children}</button>
  );
}

function SecondaryButton({ onClick, children }: { onClick?: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 'var(--btn-height)',
      padding: '0 var(--btn-padding-x)',
      borderRadius: 'var(--btn-radius)',
      backgroundColor: 'var(--color-action-secondary)',
      color: 'var(--color-action-secondary-text)',
      border: '1px solid var(--color-action-secondary-border)',
      fontFamily: 'var(--font-sans)',
      fontSize: 'var(--btn-font-size)',
      fontWeight: 'var(--font-weight-semibold)',
      cursor: 'pointer',
    }}>{children}</button>
  );
}

const smallButtonStyle: React.CSSProperties = {
  padding: 'var(--space-1) var(--space-3)',
  borderRadius: 'var(--btn-radius)',
  border: '1px solid var(--color-border-default)',
  backgroundColor: 'transparent',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  cursor: 'pointer',
};
